/**
 * WidgetConfigModal — edytor configu kafelka (tryb edycji).
 *
 * Wspólne pola (tytuł, ikona) + pola per typ widżetu (metryka KPI, encja+kubełek
 * wykresu, limity, linki szybkich akcji, treść notatki, data odliczania). Pracuje
 * na lokalnej kopii widżetu (`draft`) — zmiany lecą do kontenera dopiero przez
 * „Zapisz" (kontener wkłada zaktualizowany widżet do draftu układu, twardy zapis
 * dopiero przyciskiem Zapisz na toolbarze).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { IconPicker } from '@/components/modules/IconPicker';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { isoToLocalDateTimeInput, localDateTimeInputToIso } from '@/lib/local-date';

import {
  AMOUNT_ENTITIES,
  CHART_BUCKETS,
  CHART_VARIANTS,
  COMPANY_ENTITIES,
  DASHBOARD_ENTITIES,
  type DashboardEntity,
  type DashboardWidget,
  KPI_METRICS,
  KPI_VARIANTS,
  type QuickAction,
} from './types';
import { WIDGET_REGISTRY } from './widget-registry';

interface WidgetConfigModalProps {
  widget: DashboardWidget | null;
  onClose: () => void;
  onSave: (updated: DashboardWidget) => void;
}

const INPUT_CLASS =
  'w-full rounded-lg border border-border-secondary bg-bg-elevated px-3 py-2 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500';
const LABEL_CLASS = 'block text-xs font-semibold text-text-secondary';

export function WidgetConfigModal({ widget, onClose, onSave }: WidgetConfigModalProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DashboardWidget | null>(widget);

  useEffect(() => {
    setDraft(widget);
  }, [widget]);

  if (!draft) return null;

  // Spread configu w obrębie unii — patch typowany luźno, jeden cast na wyjściu.
  function patchConfig(patch: Record<string, unknown>) {
    setDraft((d) => (d ? ({ ...d, config: { ...d.config, ...patch } } as DashboardWidget) : d));
  }

  const entry = WIDGET_REGISTRY[draft.type];
  const iconValue = draft.config.icon ?? entry.icon;

  return (
    <Modal
      open={widget !== null}
      onClose={onClose}
      title={t('tenant.dashboard.config.title')}
      size="md"
      guardUnsaved
      // Metryki, ikony i szybkie akcje wybiera się kliknięciem (bez zdarzeń wpisywania), więc
      // „zmieniono" liczymy ze stanu (porównanie treści szkicu z widgetem), więc cofnięcie zmiany
      // ręcznie znów daje „bez zmian".
      dirty={JSON.stringify(draft) !== JSON.stringify(widget)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => onSave(draft)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className={LABEL_CLASS} htmlFor="widget-title">
            {t('tenant.dashboard.config.titleLabel')}
          </label>
          <Input
            id="widget-title"
            type="text"
            value={draft.config.title ?? ''}
            placeholder={t(entry.defaultTitleKey)}
            onChange={(e) => patchConfig({ title: e.target.value || undefined })}
          />
        </div>

        <div className="space-y-1">
          <span className={LABEL_CLASS}>{t('tenant.dashboard.config.iconLabel')}</span>
          <IconPicker value={iconValue} onChange={(name) => patchConfig({ icon: name })} />
        </div>

        {draft.type === 'kpi' && (
          <>
            <div className="space-y-1">
              <label className={LABEL_CLASS} htmlFor="kpi-metric">
                {t('tenant.dashboard.config.metricLabel')}
              </label>
              <Dropdown
                id="kpi-metric"
                value={draft.config.metric}
                onChange={(val) => patchConfig({ metric: val })}
                options={KPI_METRICS.map((m) => ({
                  value: m,
                  label: t(`tenant.dashboard.metrics.${m}`),
                }))}
              />
            </div>
            <div className="space-y-1">
              <label className={LABEL_CLASS} htmlFor="kpi-variant">
                {t('tenant.dashboard.config.kpiVariantLabel')}
              </label>
              <Dropdown
                id="kpi-variant"
                value={draft.config.variant ?? 'card'}
                onChange={(val) => patchConfig({ variant: val })}
                options={KPI_VARIANTS.map((v) => ({
                  value: v,
                  label: t(`tenant.dashboard.config.kpiVariant.${v}`),
                }))}
              />
            </div>
          </>
        )}

        {draft.type === 'transactions' && (
          <LimitField
            value={draft.config.limit ?? 5}
            onChange={(n) => patchConfig({ limit: n })}
            label={t('tenant.dashboard.config.limitLabel')}
          />
        )}

        {draft.type === 'chart' && (
          <>
            <div className="space-y-1">
              <label className={LABEL_CLASS} htmlFor="chart-entity">
                {t('tenant.dashboard.config.entityLabel')}
              </label>
              <Dropdown
                id="chart-entity"
                value={draft.config.entity}
                onChange={(val) => patchConfig({ entity: val })}
                options={DASHBOARD_ENTITIES.map((en) => ({
                  value: en,
                  label: t(`tenant.dashboard.metrics.${en}`),
                }))}
              />
            </div>
            <div className="space-y-1">
              <label className={LABEL_CLASS} htmlFor="chart-variant">
                {t('tenant.dashboard.config.variantLabel')}
              </label>
              <Dropdown
                id="chart-variant"
                value={draft.config.variant ?? 'area'}
                onChange={(val) => patchConfig({ variant: val })}
                options={CHART_VARIANTS.map((v) => ({
                  value: v,
                  label: t(`tenant.dashboard.chartVariant.${v}`),
                }))}
              />
            </div>
            {AMOUNT_ENTITIES.includes(draft.config.entity) && (
              <div className="space-y-1">
                <label className={LABEL_CLASS} htmlFor="chart-measure">
                  {t('tenant.dashboard.config.measureLabel')}
                </label>
                <Dropdown
                  id="chart-measure"
                  value={draft.config.measure ?? 'count'}
                  onChange={(val) => patchConfig({ measure: val })}
                  options={(['count', 'amount'] as const).map((m) => ({
                    value: m,
                    label: t(`tenant.dashboard.config.measure.${m}`),
                  }))}
                />
              </div>
            )}
            <div className="space-y-1">
              <label className={LABEL_CLASS} htmlFor="chart-bucket">
                {t('tenant.dashboard.config.bucketLabel')}
              </label>
              <Dropdown
                id="chart-bucket"
                value={draft.config.bucket ?? 'auto'}
                onChange={(val) => patchConfig({ bucket: val === 'auto' ? undefined : val })}
                options={[
                  { value: 'auto', label: t('tenant.dashboard.bucket.auto') },
                  ...CHART_BUCKETS.map((b) => ({
                    value: b,
                    label: t(`tenant.dashboard.bucket.${b}`),
                  })),
                ]}
              />
            </div>
            <Checkbox
              label={t('tenant.dashboard.config.compareLabel')}
              checked={draft.config.compare === true}
              onChange={(e) => patchConfig({ compare: e.target.checked })}
            />
          </>
        )}

        {draft.type === 'breakdown' && (
          <>
            <div className="space-y-1">
              <label className={LABEL_CLASS} htmlFor="breakdown-metric">
                {t('tenant.dashboard.config.metricLabel')}
              </label>
              <Dropdown
                id="breakdown-metric"
                value={draft.config.metric}
                onChange={(val) =>
                  // Podział po statusach istnieje tylko dla faktur — przy zmianie encji wróć do firm.
                  patchConfig({
                    metric: val,
                    ...(AMOUNT_ENTITIES.includes(val as DashboardEntity)
                      ? {}
                      : { dimension: undefined }),
                  })
                }
                options={DASHBOARD_ENTITIES.map((en) => ({
                  value: en,
                  label: t(`tenant.dashboard.metrics.${en}`),
                }))}
              />
            </div>
            {AMOUNT_ENTITIES.includes(draft.config.metric) && (
              <div className="space-y-1">
                <label className={LABEL_CLASS} htmlFor="breakdown-dimension">
                  {t('tenant.dashboard.config.dimensionLabel')}
                </label>
                <Dropdown
                  id="breakdown-dimension"
                  value={draft.config.dimension ?? 'company'}
                  onChange={(val) => patchConfig({ dimension: val })}
                  options={(['company', 'status'] as const).map((d) => ({
                    value: d,
                    label: t(`tenant.dashboard.config.dimension.${d}`),
                  }))}
                />
              </div>
            )}
          </>
        )}

        {draft.type === 'recent_records' && (
          <>
            <div className="space-y-1">
              <label className={LABEL_CLASS} htmlFor="recent-entity">
                {t('tenant.dashboard.config.entityLabel')}
              </label>
              <Dropdown
                id="recent-entity"
                value={draft.config.entity}
                onChange={(val) => patchConfig({ entity: val })}
                options={COMPANY_ENTITIES.map((en) => ({
                  value: en,
                  label: t(`tenant.dashboard.metrics.${en}`),
                }))}
              />
            </div>
            <LimitField
              value={draft.config.limit ?? 5}
              onChange={(n) => patchConfig({ limit: n })}
              label={t('tenant.dashboard.config.limitLabel')}
            />
          </>
        )}

        {draft.type === 'activity_feed' && (
          <LimitField
            value={draft.config.limit ?? 10}
            onChange={(n) => patchConfig({ limit: n })}
            label={t('tenant.dashboard.config.limitLabel')}
          />
        )}

        {draft.type === 'note' && (
          <div className="space-y-1">
            <span className={LABEL_CLASS}>{t('tenant.dashboard.config.noteLabel')}</span>
            <RichTextEditor
              content={draft.config.html ?? ''}
              onChange={(html) => patchConfig({ html })}
            />
          </div>
        )}

        {draft.type === 'countdown' && (
          <div className="space-y-1">
            <label className={LABEL_CLASS} htmlFor="countdown-target">
              {t('tenant.dashboard.config.targetLabel')}
            </label>
            <input
              id="countdown-target"
              type="datetime-local"
              className={INPUT_CLASS}
              value={isoToLocalDateTimeInput(draft.config.targetIso)}
              onChange={(e) =>
                patchConfig({ targetIso: localDateTimeInputToIso(e.target.value) ?? undefined })
              }
            />
          </div>
        )}

        {draft.type === 'quick_actions' && (
          <QuickActionsEditor
            actions={draft.config.actions}
            onChange={(actions) => patchConfig({ actions })}
          />
        )}
      </div>
    </Modal>
  );
}

function LimitField({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (n: number) => void;
  label: string;
}) {
  return (
    <div className="space-y-1">
      <label className={LABEL_CLASS} htmlFor="widget-limit">
        {label}
      </label>
      <Input
        id="widget-limit"
        type="number"
        min={1}
        max={50}
        value={value}
        onChange={(e) => {
          const n = Number.parseInt(e.target.value, 10);
          if (!Number.isNaN(n)) onChange(Math.min(50, Math.max(1, n)));
        }}
      />
    </div>
  );
}

function QuickActionsEditor({
  actions,
  onChange,
}: {
  actions: QuickAction[];
  onChange: (actions: QuickAction[]) => void;
}) {
  const { t } = useTranslation();

  function update(index: number, patch: Partial<QuickAction>) {
    onChange(actions.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  }

  return (
    <div className="space-y-2">
      <span className={LABEL_CLASS}>{t('tenant.dashboard.config.actionsLabel')}</span>
      {actions.map((action, i) => (
        <div key={i} className="space-y-1">
          <div className="flex items-center gap-2">
            <Input
              type="text"
              containerClassName="flex-1 min-w-0"
              placeholder={t('tenant.dashboard.config.actionLabelPlaceholder')}
              value={action.label}
              onChange={(e) => update(i, { label: e.target.value })}
            />
            <Input
              type="text"
              containerClassName="flex-1 min-w-0"
              placeholder={t('tenant.dashboard.config.actionToPlaceholder')}
              value={action.to}
              onChange={(e) => update(i, { to: e.target.value })}
            />
            <button
              type="button"
              onClick={() => onChange(actions.filter((_, idx) => idx !== i))}
              className="shrink-0 rounded p-2 text-text-muted hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
              aria-label={t('common.delete')}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <Input
            type="text"
            placeholder={t('tenant.dashboard.config.actionDescriptionPlaceholder')}
            value={action.description ?? ''}
            onChange={(e) => update(i, { description: e.target.value || undefined })}
          />
        </div>
      ))}
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<Plus className="h-4 w-4" />}
        onClick={() => onChange([...actions, { label: '', to: '' }])}
      >
        {t('tenant.dashboard.config.addAction')}
      </Button>
    </div>
  );
}
